import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaArrowRight } from 'react-icons/fa';

/** Klasik yatay hero — public/images/hero/hero1.png … hero5.png */
const HERO_IMAGES = [
  '/images/hero/hero1.png',
  '/images/hero/hero2.png',
  '/images/hero/hero3.png',
  '/images/hero/hero4.png',
  '/images/hero/hero5.png'
];

const HeroContainer = styled.section`
  height: 100vh;
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    height: 70vh;
  }
`;

const BackgroundSlider = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
`;

const BackgroundImage = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)),
    url(${props => props.src || ''}) center/cover;
  background-color: var(--primary-color);
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  max-width: 800px;
  margin: 0 auto;
  padding: 0 20px;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  color: var(--white-color);
`;

const HeroTitle = styled(motion.h1)`
  font-size: 4rem;
  font-weight: 700;
  margin-bottom: 20px;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.7);

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled(motion.p)`
  font-size: 1.5rem;
  margin-bottom: 30px;
  opacity: 0.9;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.7);

  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const HeroButtons = styled(motion.div)`
  display: flex;
  gap: 20px;
  justify-content: center;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;

const HeroButton = styled(motion.button)`
  padding: 15px 30px;
  font-size: 1.1rem;
  font-weight: 600;
  border-radius: var(--border-radius);
  cursor: pointer;
  transition: var(--transition);

  &.primary {
    background: var(--primary-color);
    color: var(--white-color);
    border: 2px solid var(--primary-color);

    &:hover {
      background: #e67e00;
      border-color: #e67e00;
    }
  }

  &.secondary {
    background: transparent;
    color: var(--white-color);
    border: 2px solid var(--white-color);

    &:hover {
      background: var(--white-color);
      color: var(--secondary-color);
    }
  }
`;

const SliderControls = styled.div`
  position: absolute;
  bottom: 30px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 20px;
  align-items: center;
  z-index: 3;
`;

const SliderButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: 2px solid rgba(255, 255, 255, 0.5);
  color: var(--white-color);
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: var(--transition);
  backdrop-filter: blur(10px);

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    border-color: rgba(255, 255, 255, 0.8);
  }
`;

const SliderDots = styled.div`
  display: flex;
  gap: 10px;
`;

const SliderDot = styled.button`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${props => props.active ? 'var(--primary-color)' : 'rgba(255, 255, 255, 0.5)'};
  border: none;
  cursor: pointer;
  transition: var(--transition);

  &:hover {
    background: ${props => props.active ? 'var(--primary-color)' : 'rgba(255, 255, 255, 0.8)'};
  }
`;

const ScrollIndicator = styled(motion.div)`
  position: absolute;
  bottom: 100px;
  left: 50%;
  transform: translateX(-50%);
  color: var(--white-color);
  font-size: 1.2rem;
  cursor: pointer;
  z-index: 2;

  &:hover {
    color: var(--primary-color);
  }
`;

const HeroSection = () => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [reduceMotion, setReduceMotion] = useState(false);

  const heroContent = [
    {
      title: 'SULTANLAR BESİCİLİK',
      subtitle: 'Modern Besi Çiftliği ve Kaliteli Hayvan Yetiştirme'
    },
    {
      title: 'Kaliteli Hizmet',
      subtitle: '25+ Yıllık Deneyim ile Profesyonel Besicilik'
    },
    {
      title: 'Sağlıklı Hayvanlar',
      subtitle: 'Veteriner Kontrolünde, Sağlık Garantili'
    },
    {
      title: 'Güvenilir Çözümler',
      subtitle: 'Müşteri Memnuniyeti Odaklı Hizmet'
    },
    {
      title: 'Çendik Köyü Burdur',
      subtitle: 'Doğal Ortamda, Kaliteli Üretim'
    }
  ];

  const slideCount = Math.min(heroContent.length, HERO_IMAGES.length);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = () => setReduceMotion(mq.matches);
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  useEffect(() => {
    if (!isAutoPlaying || slideCount < 1) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slideCount);
    }, 5000);

    return () => clearInterval(interval);
  }, [slideCount, isAutoPlaying]);

  const goToSlide = (index) => {
    if (index >= 0 && index < slideCount) {
      setCurrentSlide(index);
      setIsAutoPlaying(false);
      setTimeout(() => setIsAutoPlaying(true), 10000);
    }
  };

  const nextSlide = () => {
    if (slideCount > 0) goToSlide((currentSlide + 1) % slideCount);
  };

  const prevSlide = () => {
    if (slideCount > 0) goToSlide((currentSlide - 1 + slideCount) % slideCount);
  };

  const scrollToContent = () => {
    window.scrollTo({
      top: window.innerHeight,
      behavior: reduceMotion ? 'auto' : 'smooth'
    });
  };

  const fadeDuration = reduceMotion ? 0 : 1;

  return (
    <HeroContainer>
      <BackgroundSlider>
        <AnimatePresence mode="wait">
          <BackgroundImage
            key={currentSlide}
            src={HERO_IMAGES[currentSlide] || ''}
            initial={{ opacity: reduceMotion ? 1 : 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: reduceMotion ? 1 : 0 }}
            transition={{ duration: fadeDuration }}
          />
        </AnimatePresence>
      </BackgroundSlider>

      <HeroContent>
        <AnimatePresence mode="wait">
          <motion.div
            key={currentSlide}
            initial={{ opacity: reduceMotion ? 1 : 0, y: reduceMotion ? 0 : 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: reduceMotion ? 1 : 0, y: reduceMotion ? 0 : -50 }}
            transition={{ duration: reduceMotion ? 0 : 0.8 }}
          >
            <HeroTitle>
              {heroContent[currentSlide]?.title || 'SULTANLAR BESİCİLİK'}
            </HeroTitle>

            <HeroSubtitle>
              {heroContent[currentSlide]?.subtitle ||
                'Modern Besi Çiftliği ve Kaliteli Hayvan Yetiştirme'}
            </HeroSubtitle>

            <HeroButtons>
              <HeroButton
                type="button"
                className="primary"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/hizmetler')}
              >
                Hizmetlerimiz
              </HeroButton>
              <HeroButton
                type="button"
                className="secondary"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/iletisim')}
              >
                İletişim
              </HeroButton>
            </HeroButtons>
          </motion.div>
        </AnimatePresence>
      </HeroContent>

      <SliderControls>
        <SliderButton type="button" onClick={prevSlide} aria-label="Önceki slayt">
          <FaArrowLeft />
        </SliderButton>

        <SliderDots>
          {heroContent.slice(0, slideCount).map((_, index) => (
            <SliderDot
              key={index}
              type="button"
              active={index === currentSlide}
              onClick={() => goToSlide(index)}
              aria-label={`Slayt ${index + 1}`}
            />
          ))}
        </SliderDots>

        <SliderButton type="button" onClick={nextSlide} aria-label="Sonraki slayt">
          <FaArrowRight />
        </SliderButton>
      </SliderControls>

      <ScrollIndicator
        onClick={scrollToContent}
        animate={reduceMotion ? undefined : { y: [0, 10, 0] }}
        transition={reduceMotion ? undefined : { duration: 2, repeat: Infinity }}
      >
        ↓ Aşağı Kaydır
      </ScrollIndicator>
    </HeroContainer>
  );
};

export default HeroSection;
